import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Asset, SavingsPlan } from '@zero/shared/util-models';
import { firstValueFrom } from 'rxjs';
import { savingPlansActions } from '../+store/saving-plans.actions';
import { savingPlansFeature } from '../+store/saving-plans.reducers';
import {
  selectHasCryptoSavingsPlanSelected,
  selectHasErrors,
  selectHasMultipleIntervals,
  selectHasValidForm,
  selectHasValidOnboardingSavingsPlan,
  selectIsMultiSavingsPlanOrder,
  selectMostChosenSavingPlansInitiallyLoaded,
  selectMostChosenSavingPlansLoading,
  selectMultiSavingsPlanSum,
  selectMultiSavingsPlansAssets,
  selectMultiSavingsPlansWithAssetsNoSavings,
  selectNextExecDate,
  selectSavingsPlans,
  selectSavingsPlansDirectDebitActive,
  selectSavingsPlansOverviewInitiallyLoaded,
  selectSavingsPlansOverviewLoading,
} from '../+store/saving-plans.selectors';
import { MultiSavingsPlan } from '../entities/multi-savings-plan.entity';

@Injectable({ providedIn: 'root' })
export class SavingsPlanFacade {
  readonly #store = inject(Store);

  public readonly savingsPlans$ = this.#store.select(selectSavingsPlans);
  public readonly savingsPlansDirectDebitActive$ = this.#store.select(selectSavingsPlansDirectDebitActive);
  public readonly loading$ = this.#store.select(selectSavingsPlansOverviewLoading);
  public readonly initiallyLoaded$ = this.#store.select(selectSavingsPlansOverviewInitiallyLoaded);
  public readonly errorMessage$ = this.#store.select(savingPlansFeature.selectSavingsPlansOverviewErrorMessage);
  public readonly mostChosenSavingPlans$ = this.#store.select(savingPlansFeature.selectMostChosenSavingPlans);
  public readonly mostChosenSavingPlansLoading$ = this.#store.select(selectMostChosenSavingPlansLoading);
  public readonly mostChosenSavingPlansInitiallyLoaded$ = this.#store.select(selectMostChosenSavingPlansInitiallyLoaded);
  public readonly execDates$ = this.#store.select(savingPlansFeature.selectExecDates);
  public readonly nextExecDate$ = this.#store.select(selectNextExecDate);
  public readonly savingsPlansSubjects$ = this.#store.select(savingPlansFeature.selectSavingsPlanSubjects);
  public readonly multiSavingsPlans$ = this.#store.select(savingPlansFeature.selectMultiSavingsPlans);
  public readonly multiSavingsPlansWithAssetsNoSavings$ = this.#store.select(selectMultiSavingsPlansWithAssetsNoSavings);
  public readonly hasCryptoSavingsPlanSelected$ = this.#store.select(selectHasCryptoSavingsPlanSelected);
  public readonly execDate$ = this.#store.select(savingPlansFeature.selectExecDate);
  public readonly hasErrors$ = this.#store.select(selectHasErrors);
  public readonly multiSavingsPlansAssets$ = this.#store.select(selectMultiSavingsPlansAssets);
  public readonly isMultiSavingsPlanOrder$ = this.#store.select(selectIsMultiSavingsPlanOrder);
  public readonly multiSavingsPlanSum$ = this.#store.select(selectMultiSavingsPlanSum);
  public readonly multiSavingsPlanTotalAmount$ = this.#store.select(savingPlansFeature.selectTotalAmount);
  public readonly multiSavingsPlanIsStrategy$ = this.#store.select(savingPlansFeature.selectIsStrategy);
  public readonly confirmSavingsPlans$ = this.#store.select(savingPlansFeature.selectConfirmingSavingsPlans);
  public readonly savingsPlansCreated$ = this.#store.select(savingPlansFeature.selectSavingsPlansCreated);
  public readonly multiSavingsPlanError$ = this.#store.select(savingPlansFeature.selectMultiSavingsPlanError);
  public readonly intervals$ = this.#store.select(savingPlansFeature.selectIntervals);
  public readonly interval$ = this.#store.select(savingPlansFeature.selectInterval);
  public readonly hasMultipleIntervals$ = this.#store.select(selectHasMultipleIntervals);
  public readonly loadingExante$ = this.#store.select(savingPlansFeature.selectLoadingExante);
  public readonly exante$ = this.#store.select(savingPlansFeature.selectExante);
  public readonly hasValidForm$ = this.#store.select(selectHasValidForm);
  public readonly savingsPlanToUpdate$ = this.#store.select(savingPlansFeature.selectSavingsPlanToUpdate);
  public readonly savingsPlansUpdated$ = this.#store.select(savingPlansFeature.selectSavingsPlanUpdated);
  public readonly savingsPlansUpdateError$ = this.#store.select(savingPlansFeature.selectSavingsPlanUpdateError);
  public readonly dynamizationValues$ = this.#store.select(savingPlansFeature.selectDynamizationValues);
  public readonly dynamization$ = this.#store.select(savingPlansFeature.selectSelectedDynamization);
  public readonly selectedAsset$ = this.#store.select(savingPlansFeature.selectSelectedAsset);
  public readonly existingSavingsPlanError$ = this.#store.select(savingPlansFeature.selectExistingSavingsPlanError);
  public readonly onboardingSavingsPlanAmount$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanAmount);
  public readonly onboardingSavingsPlanIsin$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanIsin);
  public readonly onboardingSavingsPlanCached$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanCached);
  public readonly onboardingSavingsPlanAsset$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanAsset);
  public readonly onboardingSavingsPlanExante$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanExante);
  public readonly submittingOnboardingSavingsPlan$ = this.#store.select(savingPlansFeature.selectSubmittingOnboardingSavingsPlan);
  public readonly onboardingSavingsPlanCreated$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanCreated);
  public readonly onboardingSavingsPlans$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlans);
  public readonly deletingOnboardingSavingsPlan$ = this.#store.select(savingPlansFeature.selectDeletingOnboardingSavingsPlan);
  public readonly onboardingSavingsPlanDeleted$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanDeleted);
  public readonly onboardingSavingsPlanError$ = this.#store.select(savingPlansFeature.selectOnboardingSavingsPlanError);
  public readonly hasOnboardingSavingsPlans$ = this.#store.select(selectHasValidOnboardingSavingsPlan);

  public loadSavingsPlansOverview = (force = false) => this.#store.dispatch(savingPlansActions.loadSavingsPlansOverview({ force }));

  public cancelSavingsPlan = (id: string) => this.#store.dispatch(savingPlansActions.cancelSavingsPlan({ id }));

  public loadMostChosenSavingplans = (force = false, itemsCount = 15) =>
    this.#store.dispatch(savingPlansActions.loadMostChosenSavingPlans({ force, itemsCount }));

  public loadExecDates = (force = false) => this.#store.dispatch(savingPlansActions.loadExecDates({ force }));

  public loadSavingsPlanSubjects = (force = false) => this.#store.dispatch(savingPlansActions.loadSavingsPlanSubjects({ force }));

  public calcAmountBySegment = (segment: number, amountPercentage: number) => amountPercentage * (segment / 100);

  public addAssetToForm = (asset: Asset) => this.#store.dispatch(savingPlansActions.addAssetToForm({ asset }));

  public removeSavingsPlanFromForm = (savingsPlan: MultiSavingsPlan) =>
    this.#store.dispatch(savingPlansActions.removeSavingsPlansFromForm({ savingsPlansToRemove: [savingsPlan] }));

  public removeSavingsPlansFromForm = (savingsPlansToRemove: MultiSavingsPlan[]) =>
    this.#store.dispatch(savingPlansActions.removeSavingsPlansFromForm({ savingsPlansToRemove }));

  public clearForm = () => this.#store.dispatch(savingPlansActions.clearForm());

  public changeExecDate = (execDate: string) => this.#store.dispatch(savingPlansActions.changeExecDate({ execDate }));

  public updateAmount = (savingsPlan: MultiSavingsPlan, amount: number) =>
    this.#store.dispatch(savingPlansActions.updateAmount({ savingsPlan, amount }));

  public createSavingsPlans = () => this.#store.dispatch(savingPlansActions.createSavingsPlans());

  public addAssetsToForm = (totalAmount: number, savingsPlans: MultiSavingsPlan[]) =>
    this.#store.dispatch(savingPlansActions.addAssetsToForm({ totalAmount, savingsPlans }));

  public changeTotalAmount = (totalAmount: number) => this.#store.dispatch(savingPlansActions.changeTotalAmount({ totalAmount }));

  public loadOptions = () => this.#store.dispatch(savingPlansActions.loadOptions());

  public changeInterval = (interval: string) => this.#store.dispatch(savingPlansActions.changeInterval({ interval }));

  public loadExante = () => this.#store.dispatch(savingPlansActions.loadExante());

  public revokeStrategy = () => this.#store.dispatch(savingPlansActions.revokeStrategy());

  public clearMultiSavingsPlanError = () => this.#store.dispatch(savingPlansActions.clearMultiSavingsPlanError());

  public removeLatestSavingsPlan = () => this.#store.dispatch(savingPlansActions.removeLatestSavingsPlan());

  public setSavingsPlanToUpdate = (savingsPlan: SavingsPlan) =>
    this.#store.dispatch(savingPlansActions.setSavingsPlanToUpdate({ savingsPlan }));

  public updateSavingsPlan = () => this.#store.dispatch(savingPlansActions.updateSavingsPlan());

  public clearSavingsPlanUpdateError = () => this.#store.dispatch(savingPlansActions.clearSavingsPlanUpdateError());

  public setDynamization = (value: number) => this.#store.dispatch(savingPlansActions.setDynamization({ value }));

  public handleDynamizationToggle = async () => {
    const currentDynamization = await firstValueFrom(this.dynamization$);
    this.setDynamization(currentDynamization ? null : (await firstValueFrom(this.dynamizationValues$))?.[0]);
  };

  public loadExistingSavingsPlan = (id: string) => this.#store.dispatch(savingPlansActions.loadExistingSavingsPlan({ id }));

  public updateExistingSavingsPlanAmount = (amount: number) =>
    this.#store.dispatch(savingPlansActions.updateExistingSavingsPlanAmount({ amount }));

  public cacheOnboardingSavingsPlan = (amount: number, isin: string) =>
    this.#store.dispatch(savingPlansActions.cacheOnboardingSavingsPlan({ amount, isin }));

  public clearOnboardingSavingsPlanCache = () => this.#store.dispatch(savingPlansActions.clearOnboardingSavingsPlanCache());

  public loadOnboardingSavingsPlanInfos = () => this.#store.dispatch(savingPlansActions.loadOnboardingSavingsPlanInfos());

  public submitOnboardingSavingsPlan = () => this.#store.dispatch(savingPlansActions.submitOnboardingSavingsPlan());

  public loadOnboardingSavingsPlans = () => this.#store.dispatch(savingPlansActions.loadOnboardingSavingsPlans());

  public deleteOnboardingSavingsPlan = (id: string) => this.#store.dispatch(savingPlansActions.deleteOnboardingSavingsPlan({ id }));

  public clearOnboardingSavingsPlanDeletionState = () => this.#store.dispatch(savingPlansActions.clearOnboardingSavingsPlanDeletionState());

  public clearOnboardingSavingsPlanForm = () => this.#store.dispatch(savingPlansActions.clearOnboardingSavingsPlanForm());
}
